import { z } from 'zod/v4';

/**
 * Sort keys the admin feature-tags list accepts. Legacy advertised `name`,
 * `tag` and `featureId`, but every explicit sort pointed at a table alias its
 * query never declared and failed with a 500; `updatedAt` is added because the
 * old dashboard's table already offers it.
 */
export const ADMIN_DEVELOPMENT_TAG_FEATURE_SORT_COLUMNS = [
  'featureId',
  'name',
  'tag',
  'updatedAt',
] as const;

export type AdminDevelopmentTagFeatureSortColumn =
  (typeof ADMIN_DEVELOPMENT_TAG_FEATURE_SORT_COLUMNS)[number];

const sortColumns = ADMIN_DEVELOPMENT_TAG_FEATURE_SORT_COLUMNS.join('|');
// `col:DIR`, comma-separated for a multi-column sort.
const SORT_PATTERN = new RegExp(
  `^(${sortColumns}):(ASC|DESC)(,(${sortColumns}):(ASC|DESC))*$`,
);

export const AdminDevelopmentTagFeaturesQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce.number().int().min(0).max(500).default(15),
  sort: z.string().regex(SORT_PATTERN).optional(),
  /**
   * JSON object of column filters: `name` (case-insensitive substring),
   * `featureId` (exact) and `tag` (exact but case-insensitive, so the label
   * `Beta` finds `BETA`); a comma-separated `featureId` or `tag` matches any
   * of its parts. Malformed JSON and other keys are ignored, not rejected.
   * Legacy accepted the same keys but failed with a 500 on any of them.
   */
  filter: z.string().max(512).optional(),
});

export type AdminDevelopmentTagFeaturesQuery = z.infer<
  typeof AdminDevelopmentTagFeaturesQuerySchema
>;

/**
 * One feature gated behind a development tag (`active_development_tags`).
 * The table has no audit columns: `createdAt` is the feature's first
 * `entity_history` entry (`DEVELOPMENT_TAG_FEATURE`), `updatedAt` and
 * `updatedBy*` its latest — so `updatedAt` equals `createdAt` until the
 * feature changes. Legacy also sent the creator/updater ids; no screen read
 * them, so they are left out.
 */
export const AdminDevelopmentTagFeatureSchema = z.object({
  featureId: z.string(),
  name: z.string(),
  /** `EXPERIMENTAL`, `ALPHA` or `BETA`. */
  tag: z.string(),
  description: z.string().nullable(),
  createdAt: z.coerce.date().nullable(),
  updatedAt: z.coerce.date().nullable(),
  updatedByUserName: z.string().nullable(),
  updatedByCompanyName: z.string().nullable(),
});

export type AdminDevelopmentTagFeature = z.infer<
  typeof AdminDevelopmentTagFeatureSchema
>;

// The platform's paginated envelope. `pagination.page` is 1-based, unlike the
// 0-based `page` query param. Legacy sent `{ data, meta }` with a `totalCount`
// that was always 0.
export const AdminDevelopmentTagFeaturesResponseSchema = z.object({
  metadata: z.object({
    success: z.boolean(),
    pagination: z.object({
      page: z.number(),
      limit: z.number(),
      total: z.number(),
      totalPages: z.number(),
    }),
  }),
  data: z.array(AdminDevelopmentTagFeatureSchema),
});

export type AdminDevelopmentTagFeaturesResponse = z.infer<
  typeof AdminDevelopmentTagFeaturesResponseSchema
>;
